import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";

/**
 * Why a billing action was refused, for a sentence both apps translate from
 * `billing:problems.<problem>`. See api.md, Billing (staff).
 */
export type BillingProblem =
	| "network"
	| "forbidden"
	/** The plan has no billing (`FORBIDDEN`, `not_in_plan`): issuing bills and payments is off. */
	| "notInPlan"
	/** The role gives no discounts (`bill.discount`). */
	| "discountNotAllowed"
	/** More discount than the owner lets a cashier give (`limitBp`). */
	| "discountLimit"
	| "suspended"
	| "notFound"
	/** Someone billed the order meanwhile (`billId`). */
	| "orderBilled"
	/** The order was cancelled, merged or paid meanwhile. */
	| "orderClosed"
	| "nothingToBill"
	/** Quick bill: an item sold out since it was picked. */
	| "itemUnavailable"
	/** The bill came out different from what the screen showed (`totalPaise`): check it and bill again. */
	| "totalChanged"
	/** Composition or regular GST without a GSTIN: billing settings first. */
	| "gstinMissing"
	| "billNumberOverflow"
	| "billVoid"
	| "alreadyPaid"
	/** More than is left to pay (`balancePaise`). */
	| "moreThanBalance"
	/** Reverse the payments before voiding (`paidPaise`). */
	| "billHasPayments"
	/** The paid order's table has new diners: the bill cannot reopen it. */
	| "tableHasOpenOrder"
	/** The chosen bill offer no longer applies to this bill (P9-T10). */
	| "offerNotEligible"
	/** A field the server refused (`field`). */
	| "invalid"
	| "failed";

export type BillingError = {
	problem: BillingProblem;
	/** For `discountLimit`. */
	limitBp?: number;
	/** For `totalChanged`: the bill as it would be now. */
	totalPaise?: number;
	/** For `moreThanBalance`. */
	balancePaise?: number;
	/** For `billHasPayments`. */
	paidPaise?: number;
	/** For `orderBilled`. */
	billId?: string;
	/** For `invalid`. */
	field?: string;
};

const num = (v: unknown) => (typeof v === "number" ? v : undefined);

export function billingProblemOf(error: unknown): BillingError {
	const code = errorCodeOf(error);
	const d = errorDetailsOf(error);
	switch (code) {
		case "NETWORK":
			return { problem: "network" };
		case "SUBSCRIPTION_SUSPENDED":
			return { problem: "suspended" };
		case "NOT_FOUND":
			return { problem: "notFound" };
		case "ITEM_UNAVAILABLE":
			return { problem: "itemUnavailable" };
		case "VALIDATION":
			return typeof d.field === "string"
				? { problem: "invalid", field: d.field }
				: { problem: "invalid" };
		case "FORBIDDEN":
			if (d.reason === "not_in_plan") return { problem: "notInPlan" };
			if (d.reason === "discount_limit") {
				const limitBp = num(d.limitBp);
				return limitBp === undefined
					? { problem: "discountLimit" }
					: { problem: "discountLimit", limitBp };
			}
			if (d.permission === "bill.discount") return { problem: "discountNotAllowed" };
			return { problem: "forbidden" };
		case "CONFLICT":
			return conflict(d);
		default:
			return { problem: "failed" };
	}
}

function conflict(d: Record<string, unknown>): BillingError {
	switch (d.reason) {
		case "order_billed":
			return typeof d.billId === "string"
				? { problem: "orderBilled", billId: d.billId }
				: { problem: "orderBilled" };
		case "order_closed":
			return { problem: "orderClosed" };
		case "nothing_to_bill":
			return { problem: "nothingToBill" };
		case "total_changed": {
			const totalPaise = num(d.totalPaise);
			return totalPaise === undefined
				? { problem: "totalChanged" }
				: { problem: "totalChanged", totalPaise };
		}
		case "gstin_missing":
			return { problem: "gstinMissing" };
		case "offer_not_eligible":
			return { problem: "offerNotEligible" };
		case "bill_number_overflow":
			return { problem: "billNumberOverflow" };
		case "bill_void":
			return { problem: "billVoid" };
		case "already_paid":
			return { problem: "alreadyPaid" };
		case "more_than_balance": {
			const balancePaise = num(d.balancePaise);
			return balancePaise === undefined
				? { problem: "moreThanBalance" }
				: { problem: "moreThanBalance", balancePaise };
		}
		case "bill_has_payments": {
			const paidPaise = num(d.paidPaise);
			return paidPaise === undefined
				? { problem: "billHasPayments" }
				: { problem: "billHasPayments", paidPaise };
		}
		case "table_has_open_order":
			return { problem: "tableHasOpenOrder" };
		default:
			return { problem: "failed" };
	}
}
